import { useCallback } from 'react';
import { printService } from '@/Electron';
import { useDispatch, useSelector } from 'react-redux';
import { DocumentTemplateApi } from '@/Api/DocumentTemplates/DocumentTemplatesApi';
import { documentPrintModalSelectors } from '@/ModalWindows/DocumentPrintModal/services/selectors';
import { useForm } from '@/Hooks/useFormWrapper';
import { DocumentPrintOrderType } from '@/Enums/DocumentPrintOrderType';
import { DocumentPrintUnionPdfModel } from '@/Models/DocumentPrintUnionPdfModel';
import { documentPrintModalActions, DocumentPrintStep } from '@/ModalWindows/DocumentPrintModal/services/reducer';
import { useDocumentPrintModalActions } from '@/ModalWindows/DocumentPrintModal/hooks/useDocumentPrintModalActions';

export type FormData = {
  orderType: DocumentPrintOrderType;
};

export const useSelectTemplateModal = (onClose: () => void) => {
  const dispatch = useDispatch();

  const {
    register,
    getValues,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      orderType: DocumentPrintOrderType.OrderId,
    },
  });

  const { printOrderDocuments } = useDocumentPrintModalActions();

  const documentType = useSelector(documentPrintModalSelectors.documentType);
  const selectedTypeNumbers = useSelector(documentPrintModalSelectors.selectedTypeNumbers);
  const selectedTemplate = useSelector(documentPrintModalSelectors.selectedDocumentTemplate);

  const printFromArchive = useCallback(async () => {
    if (!selectedTemplate || !documentType) return;

    const { countCopy, isSortWhenPrinting } = selectedTemplate;
    const printUnionPdfModels: DocumentPrintUnionPdfModel[] = [];

    selectedTypeNumbers.forEach(({ orderId, numbers }) => {
      numbers.forEach((orderNumber) => {
        printUnionPdfModels.push({ orderId, orderNumber, countCopy });
      });
    });

    const hasMultipleNumbers = selectedTypeNumbers.some(({ numbers }) => numbers.length > 1);
    const needSort = hasMultipleNumbers && isSortWhenPrinting && countCopy != 1;
    const printOrder = getValues('orderType');

    const document = await DocumentTemplateApi.printUnionPdfFiles({
      printUnionPdfModels,
      numberGenerationType: documentType,
      documentPrintOrderType: needSort ? printOrder : undefined,
    });

    printService.printDocument(document, selectedTemplate.guid);
  }, [selectedTemplate, selectedTypeNumbers]);

  const print = handleSubmit(() => {
    if (!documentType) {
      printOrderDocuments();
      onClose();
      return;
    }

    if (selectedTypeNumbers.length == 1) {
      const documentNumbersCount = selectedTypeNumbers[0].numbers.length;

      if (documentNumbersCount == 0) {
        dispatch(documentPrintModalActions.setDocumentPrintStep(DocumentPrintStep.CreateDocument));
        return;
      } else if (documentNumbersCount > 1) {
        dispatch(documentPrintModalActions.setDocumentPrintStep(DocumentPrintStep.SelectDocumentNumber));
        return;
      }
    }

    printFromArchive();
    onClose();
  });

  return {
    methods: {
      register,
      setValue,
      print,
    },
    errors,
  };
};
